import { useState, useEffect } from 'react';
import { X, Save, Calendar, FileText, Eye, EyeOff } from 'lucide-react';
import { updateDocument, type DocumentRecord } from '../services/documentsService';
import { StarRating } from './StarRating';

interface DocumentEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  document: DocumentRecord | null;
  onSaved: () => void;
}

export default function DocumentEditModal({
  isOpen,
  onClose,
  document,
  onSaved,
}: DocumentEditModalProps) {
  const [title, setTitle] = useState('');
  const [createdAt, setCreatedAt] = useState('');
  const [showpriseclient, setShowpriseclient] = useState(false);
  const [approvedDany, setApprovedDany] = useState<number | null>(null);
  const [approvedSpecialist, setApprovedSpecialist] = useState<number | null>(null);
  const [comments, setComments] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (document && isOpen) {
      setTitle(document.title || '');
      // Formater la date pour l'input date (YYYY-MM-DD)
      if (document.created_at) {
        const date = new Date(document.created_at);
        setCreatedAt(date.toISOString().split('T')[0]);
      } else {
        setCreatedAt('');
      }
      setShowpriseclient(document.showpriseclient ?? false);
      setApprovedDany(document.approved_dany ?? null);
      setApprovedSpecialist(document.approved_specialist ?? null);
      setComments(document.comments || '');
      setError(null);
    }
  }, [document, isOpen]);

  const handleSave = async () => {
    if (!document) return;

    setIsSaving(true);
    setError(null);

    try {
      // Convertir la date en timestamp ISO
      const createdAtTimestamp = createdAt ? new Date(createdAt).toISOString() : null;

      await updateDocument(document.id, {
        title: title.trim() || undefined,
        showpriseclient,
        created_at: createdAtTimestamp || undefined, // Modifier la date de création
        approved_dany: approvedDany || null,
        approved_specialist: approvedSpecialist || null,
        comments: comments.trim() || null,
      });

      onSaved();
      onClose();
    } catch (e: any) {
      console.error('Erreur sauvegarde document:', e);
      setError(e?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen || !document) return null;
  // NOTE: Pour PRISE_BESOIN_CLIENT_2026, l'ajout de colonnes se fait dans le formulaire (prise de besoin),
  // pas via l'édition du document créé.

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-bz-dark via-bz-blue to-purple-600 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">Éditer le document</h2>
            <p className="text-sm text-white/80 mt-1">
              Modifier les informations du document
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {error && (
            <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Nom du document */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <FileText className="w-4 h-4 inline mr-2" />
              Nom du document
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Titre du document"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
            />
          </div>

          {/* Date de création */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <Calendar className="w-4 h-4 inline mr-2" />
              Date de création
            </label>
            <input
              type="date"
              value={createdAt}
              onChange={(e) => setCreatedAt(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
            />
            <p className="text-xs text-gray-500 mt-1">
              Date originale: {document.created_at ? new Date(document.created_at).toLocaleDateString('fr-CA') : 'N/A'}
            </p>
          </div>

          {/* Visibilité client */}
          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={showpriseclient}
                onChange={(e) => setShowpriseclient(e.target.checked)}
                className="w-5 h-5 rounded border-gray-300 text-bz-blue focus:ring-2 focus:ring-bz-blue"
              />
              <div className="flex items-center gap-2">
                {showpriseclient ? (
                  <Eye className="w-5 h-5 text-emerald-600" />
                ) : (
                  <EyeOff className="w-5 h-5 text-gray-400" />
                )}
                <span className="text-sm font-semibold text-gray-700">
                  Afficher ce document dans l'espace client PRIsME
                </span>
              </div>
            </label>
            <p className="text-xs text-gray-500 mt-2 ml-8">
              Si coché, ce document sera visible par le client dans son espace PRIsME.
            </p>
          </div>

          {/* Validation Dany */}
          <div className="border-t border-gray-200 pt-6">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              ⭐ Validation Dany
            </label>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <StarRating
                rating={approvedDany || undefined}
                interactive={true}
                onRatingChange={(rating) => setApprovedDany(rating)}
                size="lg"
              />
              <div className="mt-3 text-xs text-gray-600 space-y-1">
                <p><strong>Légende:</strong></p>
                <p>1 ⭐ - Premier brouillon</p>
                <p>2 ⭐ - Commencé mais pas prêt pour la prod</p>
                <p>3 ⭐ - On peut le mettre en production (≥ 50%)</p>
                <p>4 ⭐ - Satisfait mais manque un petit quelque chose (75%)</p>
                <p>5 ⭐ - Tout est sur la coche (100%)</p>
              </div>
            </div>
          </div>

          {/* Validation Spécialiste */}
          <div className="border-t border-gray-200 pt-6">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              ⭐ Validation Spécialiste
            </label>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <StarRating
                rating={approvedSpecialist || undefined}
                interactive={true}
                onRatingChange={(rating) => setApprovedSpecialist(rating)}
                size="lg"
              />
              <div className="mt-3 text-xs text-gray-600 space-y-1">
                <p><strong>Légende:</strong></p>
                <p>1 ⭐ - Premier brouillon</p>
                <p>2 ⭐ - Commencé mais pas prêt pour la prod</p>
                <p>3 ⭐ - On peut le mettre en production (≥ 50%)</p>
                <p>4 ⭐ - Satisfait mais manque un petit quelque chose (75%)</p>
                <p>5 ⭐ - Tout est sur la coche (100%)</p>
              </div>
            </div>
          </div>

          {/* Commentaires */}
          <div className="border-t border-gray-200 pt-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Commentaires
            </label>
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Ajouter des commentaires sur ce document..."
              rows={5}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bz-blue focus:border-bz-blue resize-none"
            />
            <p className="text-xs text-gray-500 mt-1">
              Notes et commentaires sur l'état du document, les améliorations à apporter, etc.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-100 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 rounded-lg bg-bz-blue text-white font-semibold hover:bg-bz-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {isSaving ? 'Sauvegarde...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

